import isUndefined from 'lodash/isUndefined';
import uuid from 'uuid/v4';
import Auth from '@Services/Auth';
import { MARVIN_TEXTS } from '@Constants/MarvinConstants';
import StructureContainers from '@Services/StructureContainers';
import MarvinHelpers from '@Services/MarvinHelpers';
import History from '@Services/History';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { setDrawingFromEditor } from '@Containers/ResultsFilters/ResultsFiltersComponents/StructureFilter/StructureFilterActions';
import { currentOptionsNameValueObjectsArray as currentOptionsNameValueObjectsArraySelector } from '@Containers/StructureOptions/StructureOptionsSelecors';
import { updateSavedInfoWithCurrent as updateSavedInfoWithCurrentAction } from '@Containers/StructureOptions/StructureOptionsActions';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import { structureName as structureNameSelector } from './selectors';
import { SET_CURRENT_EDITOR, SET_STRUCTURE, CLEAR_STRUCTURE, CLEAR_STATE } from './constants';

export const setCurrentEditor = (editor) => ({
  type: SET_CURRENT_EDITOR,
  payload: { currentEditor: editor },
});

export const setStructure = ({ structure, structureName, structureInsertMode }) => ({
  type: SET_STRUCTURE,
  payload: { structure, structureName, structureInsertMode, changeStructureTriggerUid: uuid() },
});

export const clearStructure = () => ({
  type: CLEAR_STRUCTURE,
});

export const initialize =
  ({ structureUid }) =>
  async (dispatch) => {
    try {
      const { data: structure, name: structureName } = await StructureContainers.getContainerByUID(structureUid);
      if (structure) {
        dispatch(setStructure({ structure, structureName }));
      }
    } catch (e) {
      Logger.error(e);
    }
  };

export const onEditorChange =
  ({ prevEditor, nextEditor, structure }) =>
  (dispatch) => {
    if (prevEditor !== nextEditor && structure) {
      dispatch(setStructure({ structure }));
    }
  };

function resolveStates({ dataFromEditor, editorExportTarget, keyForStructureData }) {
  return (dispatch) => {
    if (editorExportTarget === 'quickSearch' || isUndefined(editorExportTarget)) {
      history.push({
        pathname: paths.SEARCH,
        state: { trackAACustom: 'force' },
      });
    } else if (editorExportTarget === 'advancedSearch') {
      history.push({
        pathname: paths.ADVANCED,
        state: { trackAACustom: 'force' },
      });
    } else if (editorExportTarget === 'results') {
      if (dataFromEditor) {
        const { data, preview, options } = dataFromEditor;

        dispatch(
          setDrawingFromEditor(
            {
              structure: data,
              preview,
              options,
            },
            keyForStructureData
          )
        );
      }

      History.goToResultPage();
    }
  };
}

export const transfer =
  ({ data, structureUid, editorExportTarget, context, keyForStructureData }) =>
  (dispatch, getState) => {
    const structureOptions = currentOptionsNameValueObjectsArraySelector(getState(), { id: structureUid });
    const structureName = structureNameSelector(getState());
    const isReaction = MarvinHelpers.isReaction(data.data);
    const isResults = editorExportTarget === 'results';
    const isReactionsContext = context === 'reactions';
    const isRMCLicense = Auth.isRMCLicense();

    if (isReaction && isRMCLicense) {
      NotifyDialogsService.openModalWindow({
        ...MARVIN_TEXTS.notAllowedReactionLicense,
        trackingOptions: {
          component: 'Structure editor',
        },
      });
      return;
    }

    if (isReaction && isResults && !isReactionsContext) {
      NotifyDialogsService.openModalWindow({
        ...MARVIN_TEXTS.notAllowedReactionFilter,
        trackingOptions: {
          component: 'Structure editor',
        },
      });
      return;
    }

    const dataFromEditor = {
      data: data.data,
      preview: data.preview,
      options: structureOptions,
      name: structureName,
    };

    try {
      dispatch(updateSavedInfoWithCurrentAction(structureUid));
      StructureContainers.saveContainer(structureUid, dataFromEditor);
      dispatch(resolveStates({ dataFromEditor, editorExportTarget, keyForStructureData }));
    } catch (e) {
      Logger.error(e);
    }
  };

export const cancel =
  ({ editorExportTarget }) =>
  (dispatch) => {
    dispatch(resolveStates({ editorExportTarget }));
  };

export const clearState = () => ({
  type: CLEAR_STATE,
});
